<script setup>
    /**
     * Vue Scripts
     */
    import {computed, ref, onMounted, onUnmounted} from "vue"
    import {glossaryStore} from "@pinia/glossary-store"
    import {apiFetch, updateLoading} from "@util/api";

    /**
     * Vue Components
     */
    import GlossaryItem from "./Components/GlossaryItem/GlossaryItem.vue"
    import LoadingIcon from "@component/Loading/Loading.vue"

    const {glossaryApi} = API
    const store = glossaryStore()

    const scrollComponent = ref(null);

    const loading = computed(() => store.loading)
    const results = computed(() => store.results)
    const query = computed(() => store.query)
    const maxPages = computed(() => store.maxPages)

    /**
     * Initial fetching of the Glossary Terms
     * and binding the scroll event handler
     * to automatically load more
     */
    onMounted(() => {
        updateLoading(store, true)
        fetchGlossaryTerms()
        window.addEventListener('scroll', handleTermsScrolling)
    })

    /**
     * Remove the Infinite Scroll Listener
     */
    onUnmounted(() => window.removeEventListener('scroll', handleTermsScrolling))

    /**
     * Handles the page scrolling of the element, and fetches
     * the next page of Glossary Terms from the API unless
     * we are already fetching, or we are already on the last
     * page of the results
     */
    const handleTermsScrolling = () => {
        const element = scrollComponent.value
        // Check the bottom value times 50% is lower than the windows innerHeight
        const triggerScrollThreshold = (element.getBoundingClientRect().bottom * 1.5) < window.innerHeight;

        /**
         * Load only if we're not on the last of pages, the
         * component isn't already fetching, and the user
         * has reached the last item in the list
         */
        (triggerScrollThreshold && !loading.value && (query.value.page !== maxPages.value)) && loadMoreTerms()
    }

    /**
     * API call to fetch the next bit of
     * Glossary Terms from the Rest API
     */
    const loadMoreTerms = () => {
        store.$patch(
            {
                query: {
                    ...store.query,
                    page: query.value.page + 1,
                },
                loading: true,
            }
        )

        fetchGlossaryTerms()
    }

    /**
     * Our API call to fetch Glossary Terms
     */
    const fetchGlossaryTerms = () => {
        apiFetch(glossaryApi, query.value,)
            .then(({data, status}) => status === 200 && store.$patch({
                results: [
                    ...store.results,
                    ...data.results
                ],
                total: data.total,
                maxPages: data.maxPages,
            }))
            .finally(() => updateLoading(store, false))
    }

    /**
     * Handles returning the string if there's a
     * search term but no results, or if there's
     * no results on letter filtering
     *
     * @returns {string|*}
     */
    const noSearchText = () => {
        const {noGlossaryTerm, noGlossaryItems} = GLOSSARY?.results || {};
        const search = query.value?.search || '';

        return search ? `${noGlossaryTerm} ${search}` : (noGlossaryItems || '');
    };

</script>

<template>

    <div
        ref="scrollComponent"
        class="glossary-results"
    >
        <ul class="glossary-list">
            <!-- Results List -->
            <GlossaryItem
                v-if="results.length"
                v-for="({title, content}, index) in results"
                :key="index"
                :content="content"
                :index="index"
                :title="title"
            />

            <!-- Loading -->
            <li
                v-if="loading"
                class="infinite-scroll-loading"
            >
                <LoadingIcon :loading="loading" />
            </li>

            <!-- Handle No Results Found String -->
            <li
                v-if="!results.length && !loading"
                class="glossary-item"
            >
                <p v-html="noSearchText()" />
            </li >
        </ul>
    </div>

</template>

<script>
    export default {
        name: "Glossary Results"
    }
</script>

<style scoped lang="scss">

    .glossary {
        &-results {
            position: relative;
        }
        &-list {
            list-style-type: none;
            padding: 0;
        }
        &-item {
            padding: 10px;
            border-bottom: 1px solid #eee;
        }
        &-scroll-to-top {
            position: absolute;
            bottom: 0;
            right: 0;
            z-index: 10;
        }
    }

    .infinite-scroll-loading {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }
</style>
